import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getEloTier } from '../../../utils/eloUtils';

interface RankingUser {
  id: string;
  username: string;
  elo_rating: number;
  avatar_url?: string;
}

interface RankingListProps {
  users: RankingUser[];
  currentUserId?: string;
  preview?: boolean;
  context?: 'challenges' | 'home';
}

const RankingList: React.FC<RankingListProps> = ({ 
  users = [],
  currentUserId, 
  preview = true,
  context = 'home' 
}) => {
  const { t } = useTranslation();
  
  // Ensure users is always an array
  const safeUsers = Array.isArray(users) ? users : [];
  const displayUsers = preview ? safeUsers.slice(0, 5) : safeUsers;

  const translations = {
    title: context === 'challenges' 
      ? t('challenges.ranking.title') 
      : t('ranking.ranking'),
    description: context === 'challenges'
      ? t('challenges.ranking.description')
      : t('ranking.description'),
    viewFull: context === 'challenges'
      ? t('challenges.ranking.viewFull')
      : t('ranking.viewFull'),
    elo: context === 'challenges'
      ? t('challenges.ranking.elo')
      : t('ranking.elo'),
    moreUsers: context === 'challenges'
      ? t('challenges.ranking.moreUsers', { count: Math.max(0, safeUsers.length - 5) })
      : t('ranking.moreUsers', { count: Math.max(0, safeUsers.length - 5) })
  };

  return (
    <div className="bg-base-100/95 backdrop-blur-sm rounded-box p-3 border border-info/20 shadow-lg w-full">
      <div className="flex justify-between items-center mb-3">
        <div>
          <h3 className="text-base font-bold text-info">{translations.title}</h3>
          {context === 'challenges' && (
            <p className="text-xs text-base-content/90">{translations.description}</p>
          )}
        </div>
        {preview && (
          <Link 
            to="/ranking" 
            className="btn btn-ghost btn-xs normal-case gap-1 text-info"
          >
            {translations.viewFull}
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {displayUsers.map((user, index) => {
          const tier = getEloTier(user.elo_rating);
          const position = index + 1;
          const medal = position <= 3 ? ['🥇', '🥈', '🥉'][position - 1] : null;
          const isCurrentUser = user.id === currentUserId;

          return (
            <div 
              key={user.id}
              className={`flex items-center gap-1 p-2 rounded-lg ${
                isCurrentUser ? 'bg-info/10' : 'bg-base-200/80'
              } hover:bg-base-100 transition-colors overflow-hidden`}
            >
              {/* Position */}
              <div className="w-6 flex-shrink-0 text-center font-bold opacity-70">
                {medal || `#${position}`}
              </div>

              {/* User info */}
              <div className="flex-1 min-w-0 flex items-center gap-1">
                <div className="avatar flex-shrink-0">
                  <div className="w-6 h-6 rounded-full ring ring-info ring-offset-base-100 ring-offset-1">
                    {user.avatar_url ? (
                      <img 
                        src={user.avatar_url} 
                        alt={user.username || 'User'}
                        className="object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = `https://api.dicebear.com/7.x/bottts/svg?seed=${user.username || 'user'}`;
                        }}
                      />
                    ) : (
                      <img 
                        src={`https://api.dicebear.com/7.x/bottts/svg?seed=${user.username || 'user'}`}
                        alt={user.username || 'User'}
                      />
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 min-w-0 flex-1">
                  <div className="w-4 h-4 flex-shrink-0">
                    <svg viewBox="0 0 24 24" fill={tier.color}>
                      <path d={tier.icon} />
                    </svg>
                  </div>
                  <span className="font-medium truncate text-sm">
                    {user.username ? (user.username.length > 8 ? user.username.substring(0, 8) + '...' : user.username) : 'User'}
                  </span>
                </div>
              </div>

              {/* ELO */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <span className="text-xs opacity-70 hidden xs:inline">{translations.elo}</span>
                <span className="font-bold text-xs">{user.elo_rating}</span>
              </div>
            </div>
          );
        })}

        {preview && safeUsers.length > 5 && (
          <div className="text-center text-base-content/60 text-sm mt-2">
            {translations.moreUsers}
          </div>
        )}
      </div>
    </div>
  );
};

export default RankingList;
